import { DOCUMENT, Injectable, NgZone, inject } from "@angular/core";
import { IdentityService } from "./identity.service";
import { CHATBOT_CONFIG } from "../config";

/**
 * Sent by the chatbot to ask the host for the current user's credentials.
 *
 * Both names are frozen. They are the wire of a protocol whose other half lives in every host,
 * so renaming one breaks every host that has not been redeployed in the same release. They carry
 * the project's current name and will keep it until that name changes everywhere at once.
 */
export const CREDENTIALS_REQUEST = "atl-chatbot:credentials-request";
/** Sent by the host in reply, carrying the current user's session token. */
export const CREDENTIALS_RESPONSE = "atl-chatbot:credentials";

interface CredentialsMessage {
    type: typeof CREDENTIALS_RESPONSE;
    token?: string | null;
}

/**
 * Obtains the authenticated domains' credentials from the host.
 *
 * The backend verifies a bearer token for every domain except the anonymous one, so the chatbot
 * needs the host's session token. It is requested over `postMessage` rather than read from the
 * URL: a query parameter would put a live session token in the address bar, in the referrer, and
 * in the host's browser history.
 *
 * Contract for every host, whether it mounts the chatbot in its own page or loads it in a frame:
 *
 * ```js
 * // The widget asks on startup; the host may also push credentials unprompted, on login.
 * window.addEventListener('message', (event) => {
 *   if (event.origin !== CHATBOT_ORIGIN) return;
 *   if (event.data?.type !== 'atl-chatbot:credentials-request') return;
 *   target.postMessage({ type: 'atl-chatbot:credentials', token: sessionToken }, CHATBOT_ORIGIN);
 * });
 * ```
 *
 * Only the token. The backend reads who the caller is out of it, so an account id from the host
 * would be a value nothing checks. The anonymous domain never reaches this service at all.
 */
@Injectable({ providedIn: "root" })
export class HostBridgeService {
    private readonly identity = inject(IdentityService);
    private readonly config = inject(CHATBOT_CONFIG);
    private readonly zone = inject(NgZone);
    private readonly document = inject(DOCUMENT);
    private listener?: (event: MessageEvent) => void;

    /**
     * Starts listening for credentials and asks the host for them.
     *
     * A domain with no credential needs nothing from the host, so the listener is never installed
     * there. That keeps the one unauthenticated surface from having a message channel at all.
     *
     * It is the host's own domain that decides this, not whichever identity is current: this
     * channel carries the host's credential, and a test identity signed into elsewhere neither
     * arrives here nor replaces it.
     */
    connect(): void {
        if (this.identity.hostRules()?.identity !== "bearer") {
            return;
        }

        const allowedOrigins = this.config.allowedHostOrigins;

        const view = this.document.defaultView;
        if (!view) {
            return;
        }

        this.listener = (event: MessageEvent) => {
            // Only the configured hosts may set credentials: without this check any page that can
            // reach this window could impersonate a user by posting its own id and token.
            if (!allowedOrigins.includes(event.origin)) {
                return;
            }

            const data = event.data as CredentialsMessage | null;
            if (!data || data.type !== CREDENTIALS_RESPONSE) {
                return;
            }

            // postMessage callbacks run outside Angular's zone; signals set here must be applied
            // inside it so the chat reacts to credentials arriving.
            this.zone.run(() => this.identity.setCredentials(data.token ?? null));
        };

        view.addEventListener("message", this.listener);

        // The host may not be listening yet when we start, so ask explicitly.
        if (view.parent && view.parent !== view) {
            // Loaded in a frame: ask each allowed origin across the frame boundary.
            for (const origin of allowedOrigins) {
                view.parent.postMessage({ type: CREDENTIALS_REQUEST }, origin);
            }
        } else {
            // Mounted directly in the host's page: same window, no frame boundary. `postMessage`
            // to `self` still works and is what lets the host answer without polling. Needed
            // every time this component remounts, not just once at login, because the host's own
            // credentials push at login time is missed by a widget that opens later.
            view.postMessage({ type: CREDENTIALS_REQUEST }, view.location.origin);
        }
    }

    disconnect(): void {
        const view = this.document.defaultView;
        if (this.listener && view) {
            view.removeEventListener("message", this.listener);
            this.listener = undefined;
        }
    }
}
